import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import type { BenchmarkRecord, BenchmarkRequest } from '../models/benchmark.types'

import { appHttp, decodeDto, unwrap } from '@/shared/http'

const FEATURE_OWNED_ERRORS: AxiosRequestConfig = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
}

/** D11: replace only invalid JSON values anchored after a property separator. */
export function sanitizeBenchmarkBody(raw: string): string {
  return raw.replace(/:\s*(?:-?Infinity|NaN)\b/gu, ': 0')
}

/** Axios transform entry point; malformed bodies remain visible to the typed decoder. */
export function parseBenchmarkBody(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(sanitizeBenchmarkBody(raw)) as unknown
  } catch {
    return raw
  }
}

function decodeBenchmarkRecords(value: unknown): BenchmarkRecord[] {
  if (!Array.isArray(value)) throw new TypeError('Malformed creative benchmark response')
  return value.map((entry) => {
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
      throw new TypeError('Malformed creative benchmark response')
    }
    const record = entry as Record<string, unknown>
    const info = record['info']
    const metrics = record['metrics']
    if (
      typeof info !== 'object' ||
      info === null ||
      Array.isArray(info) ||
      !['string', 'number'].includes(typeof (info as Record<string, unknown>)['ad_id']) ||
      typeof metrics !== 'object' ||
      metrics === null ||
      Array.isArray(metrics) ||
      Object.values(metrics).some((metric) => typeof metric !== 'number')
    ) {
      throw new TypeError('Malformed creative benchmark response')
    }
    return entry as BenchmarkRecord
  })
}

/**
 * TikTok creative percentile benchmark — contract 27 §6.1 #3
 * (v3 `CreativeBenchmarkSection.vue:205`).
 *
 * The response is FLAT, not enveloped: v3 reads `res.data` straight into the record array, so
 * this unwraps rather than unwrapping an envelope. (Contract §6.1's table header calls the whole
 * `appHttp` group "enveloped"; this endpoint is not — the api sketch in the same section uses
 * `unwrap`, and the v3 call site agrees.)
 *
 * v3 reaches this endpoint from inside an axios REQUEST/RESPONSE INTERCEPTOR installed by a
 * component (`:220–317`) so it can rewrite the controller URL and await the join. That is the
 * single worst defect in module 27 and it does not come across: this class is a plain call, and
 * the join happens in a pure `mergeBenchmarkRows`.
 */
export class CreativeBenchmarkApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  /** Cancellation is the caller's business — the signal rides through; nothing is swallowed here. */
  async fetchBenchmark(
    workspaceId: number,
    request: BenchmarkRequest,
    signal?: AbortSignal,
  ): Promise<BenchmarkRecord[]> {
    const config: AxiosRequestConfig = {
      ...FEATURE_OWNED_ERRORS,
      transformResponse: [parseBenchmarkBody],
    }
    if (signal) config.signal = signal
    const response = await this.http.post(
      `/tiktok-creative-benchmark/${workspaceId}/`,
      request,
      config,
    )
    return decodeDto(unwrap<unknown>(response), decodeBenchmarkRecords)
  }
}
